import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { Vehicle } from '../vehicle/vehicle.model';
import { VehicleService } from '../vehicle/vehicle.service';
import {
  TruckOperationalSignalRow,
  TruckPnlBucket,
  TruckPnlTransactionRow,
  TruckPnlTrendPoint,
  TruckProfitabilityDashboard,
  VehicleAccountingProfile
} from '../fleet-analytics.model';
import { FleetAnalyticsService } from '../fleet-analytics.service';

@Component({
  selector: 'app-truck-profitability',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ProgressBarModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './truck-profitability.component.html',
  styleUrls: ['./truck-profitability.component.css']
})
export class TruckProfitabilityComponent implements OnInit {
  loading = false;
  savingProfile = false;
  vehicleOptions: Vehicle[] = [];
  selectedVehicleId?: number;
  dateRange: Date[] = [this.addMonths(new Date(), -1), new Date()];
  basis: 'ACCOUNTING' | 'OPERATIONAL_PREVIEW' = 'ACCOUNTING';
  dashboard?: TruckProfitabilityDashboard;
  profileDialogVisible = false;
  profileForm: VehicleAccountingProfile = this.emptyProfile();

  readonly basisOptions = [
    { label: 'Accounting', value: 'ACCOUNTING' },
    { label: 'Operational Preview', value: 'OPERATIONAL_PREVIEW' }
  ];

  constructor(
    private readonly fleetAnalyticsService: FleetAnalyticsService,
    private readonly vehicleService: VehicleService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadVehicleOptions();
  }

  get metrics(): PageMetric[] {
    const summary = this.dashboard?.summary;
    const netProfit = Number(summary?.netProfit ?? 0);
    return [
      { label: 'Revenue', value: this.money(summary?.revenue), hint: 'Vehicle-dimension income', tone: 'info' },
      { label: 'Operating Cost', value: this.money(summary?.operatingCost), hint: 'Direct running costs', tone: 'warn' },
      { label: 'Ownership Cost', value: this.money(summary?.ownershipCost), hint: 'Depreciation and asset costs', tone: 'default' },
      { label: 'Net Truck Profit', value: this.money(netProfit), hint: this.percent(summary?.profitMarginPercent), tone: netProfit < 0 ? 'danger' : 'success' }
    ];
  }

  loadVehicleOptions(): void {
    const company = this.sessionStorageService.getCompany();
    const companyId = company?.id ?? 0;
    const agencyId = company?.transportationAgencyId ?? this.sessionStorageService.getTransportationAgency()?.rowId ?? 0;
    if (!companyId) {
      this.messageService.add({ severity: 'warn', summary: 'Company missing', detail: 'Unable to resolve the current company.' });
      return;
    }
    this.loading = true;
    forkJoin({
      owned: agencyId ? this.vehicleService.findVehiclesByTransportationAgencyId(0, 500, agencyId).pipe(catchError(() => of(this.emptyList<Vehicle>()))) : of(this.emptyList<Vehicle>()),
      freelance: this.vehicleService.getFreelanceVehiclesByCompanyId(0, 500, companyId).pipe(catchError(() => of(this.emptyList<Vehicle>()))),
      rental: this.vehicleService.getRentalVehiclesByCompanyId(0, 500, companyId).pipe(catchError(() => of(this.emptyList<Vehicle>())))
    }).pipe(finalize(() => this.loading = false)).subscribe(result => {
      this.vehicleOptions = this.dedupeVehicles([
        ...(result.owned.items ?? []),
        ...(result.freelance.items ?? []),
        ...(result.rental.items ?? [])
      ]);
      if (!this.selectedVehicleId && this.vehicleOptions.length) {
        this.selectedVehicleId = this.vehicleOptions[0].vehicleId;
      }
      if (this.selectedVehicleId) {
        this.loadDashboard();
      }
    });
  }

  loadDashboard(): void {
    if (!this.selectedVehicleId) {
      this.dashboard = undefined;
      return;
    }
    this.loading = true;
    this.fleetAnalyticsService.getTruckDashboard({
      vehicleId: this.selectedVehicleId,
      periodFrom: this.dateRange?.[0],
      periodTo: this.dateRange?.[1],
      basis: this.basis
    }).pipe(finalize(() => this.loading = false)).subscribe({
      next: dashboard => this.dashboard = dashboard,
      error: () => this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Unable to load Truck P&L.' })
    });
  }

  clearFilters(): void {
    this.basis = 'ACCOUNTING';
    this.dateRange = [this.addMonths(new Date(), -1), new Date()];
    this.loadDashboard();
  }

  openProfileDialog(): void {
    const profile = this.dashboard?.profile ?? this.emptyProfile();
    this.profileForm = {
      ...profile,
      vehicleId: this.selectedVehicleId,
      acquisitionDate: this.toDate(profile.acquisitionDate),
      depreciationStartDate: this.toDate(profile.depreciationStartDate),
      depreciationMethod: profile.depreciationMethod || 'STRAIGHT_LINE'
    };
    this.profileDialogVisible = true;
  }

  saveProfile(): void {
    if (!this.selectedVehicleId) {
      return;
    }
    this.savingProfile = true;
    this.fleetAnalyticsService.saveVehicleAccountingProfile(this.selectedVehicleId, this.profileForm)
      .pipe(finalize(() => this.savingProfile = false))
      .subscribe({
        next: response => {
          this.profileDialogVisible = false;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: response.message || 'Vehicle accounting profile saved.' });
          this.loadDashboard();
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Unable to save the vehicle accounting profile.' })
      });
  }

  selectedVehicleLabel(): string {
    const vehicle = this.vehicleOptions.find(option => option.vehicleId === this.selectedVehicleId) ?? this.dashboard?.vehicle;
    return this.vehicleLabel(vehicle);
  }

  vehicleLabel(vehicle?: Vehicle | null): string {
    if (!vehicle) {
      return 'Select truck';
    }
    return [vehicle.plateNumber, vehicle.registrationNumber].filter(Boolean).join(' | ') || `Vehicle #${vehicle.vehicleId}`;
  }

  bucketRows(bucket: string): TruckPnlBucket[] {
    return (this.dashboard?.pnlBuckets ?? []).filter(row => row.bucket === bucket);
  }

  transactions(): TruckPnlTransactionRow[] {
    return this.dashboard?.transactions ?? [];
  }

  operationalSignals(): TruckOperationalSignalRow[] {
    return this.dashboard?.operationalSignals ?? [];
  }

  trendRows(): TruckPnlTrendPoint[] {
    return this.dashboard?.trend ?? [];
  }

  trendWidth(row: TruckPnlTrendPoint, key: keyof TruckPnlTrendPoint): string {
    const max = Math.max(1, ...this.trendRows().flatMap(item => [
      Math.abs(Number(item.revenue ?? 0)),
      Math.abs(Number(item.operatingCost ?? 0)),
      Math.abs(Number(item.ownershipCost ?? 0)),
      Math.abs(Number(item.netProfit ?? 0))
    ]));
    return `${Math.max(4, Math.min(100, (Math.abs(Number(row[key] ?? 0)) / max) * 100))}%`;
  }

  sourceSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'POSTED': return 'success';
      case 'CALCULATED_NOT_POSTED': return 'warn';
      case 'UNPOSTED_OPERATIONAL': return 'info';
      default: return 'secondary';
    }
  }

  bucketSeverity(bucket?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (bucket) {
      case 'REVENUE': return 'success';
      case 'OPERATING_COST': return 'warn';
      case 'OWNERSHIP_COST': return 'info';
      case 'ALLOCATED_OVERHEAD': return 'secondary';
      default: return 'secondary';
    }
  }

  pretty(value?: string): string {
    return (value || '-').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase());
  }

  money(value?: number | null): string {
    return `${Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;
  }

  percent(value?: number | null): string {
    return `${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }

  date(value?: string | Date | null): string {
    if (!value) {
      return '-';
    }
    return new Date(value).toLocaleDateString();
  }

  private dedupeVehicles(vehicles: Vehicle[]): Vehicle[] {
    const map = new Map<number, Vehicle>();
    vehicles.filter(vehicle => vehicle?.vehicleId).forEach(vehicle => {
      if (!map.has(vehicle.vehicleId)) {
        map.set(vehicle.vehicleId, vehicle);
      }
    });
    return [...map.values()].sort((left, right) => this.vehicleLabel(left).localeCompare(this.vehicleLabel(right)));
  }

  private emptyList<T>(): APIListWrapper<T> {
    return { items: [], count: 0, limit: 0, offset: 0, hasMore: false } as APIListWrapper<T>;
  }

  private emptyProfile(): VehicleAccountingProfile {
    return {
      depreciationMethod: 'STRAIGHT_LINE',
      salvageValue: 0
    };
  }

  private addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  }

  private toDate(value?: string | Date | null): Date | null {
    return value ? new Date(value) : null;
  }
}
